import { inject, Injectable } from '@angular/core';
import { KPI_EXCLUDES_ON_TRACK_IN_COUNT } from '@catalogues';
import { KpiChartData, KpiChartId, KpiKeys } from '@models';
import { Observable } from 'rxjs';

import { KpiChartCalculationService } from '../kpi-chart-calculation/kpi-chart-calculation.service';
import { KpiChartDataStoreService } from '../kpi-chart-data-store/kpi-chart-data-store.service';
import { KpiChartFilterService } from '../kpi-chart-filter/kpi-chart-filter.service';

@Injectable({
  providedIn: 'root'
})
export class KpiChartFacadeService {
  private kpiChartDataStore = inject(KpiChartDataStoreService);
  private kpiChartFilter = inject(KpiChartFilterService);
  // Note: Keep calculation service reference for future use
  private kpiChartCalculation = inject(KpiChartCalculationService);

  get displayValues$(): Observable<{
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  }> {
    return this.kpiChartDataStore.displayValues$;
  }

  get filterService() {
    return this.kpiChartFilter;
  }

  get kpiChartData$(): Observable<Partial<Record<KpiKeys, KpiChartData>>> {
    return this.kpiChartDataStore.kpiChartData$;
  }

  get activeKpiKey$(): Observable<KpiKeys | null> {
    return this.kpiChartDataStore.activeKpiKey$;
  }

  get activeKpiChartData$(): Observable<KpiChartData | null> {
    return this.kpiChartDataStore.activeKpiChartData$;
  }

  get isActiveKpiLoading$(): Observable<boolean> {
    return this.kpiChartDataStore.isActiveKpiLoading$;
  }

  get toggleStates$(): Observable<Partial<Record<KpiKeys, Record<KpiChartId, KpiChartId>>>> {
    return this.kpiChartDataStore.toggleStates$;
  }

  syncChartData(data: Partial<Record<KpiKeys, KpiChartData>>): void {
    this.kpiChartDataStore.syncKpiChartData(data);
  }

  syncActiveKpi(kpiKey: KpiKeys | null): void {
    this.kpiChartDataStore.syncActiveKpiKey(kpiKey);
  }

  syncLoadingState(kpiKey: KpiKeys, isLoading: boolean): void {
    this.kpiChartDataStore.syncLoadingState(kpiKey, isLoading);
  }

  syncDisplayValues(values: {
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  }): void {
    this.kpiChartDataStore.syncDisplayValues(values);
  }

  setToggleState(kpiKey: KpiKeys, toggleChartId: KpiChartId, selectedChartId: KpiChartId): void {
    this.kpiChartDataStore.setToggleState(kpiKey, toggleChartId, selectedChartId);
  }

  getToggleState(kpiKey: KpiKeys, toggleChartId: KpiChartId): KpiChartId | null {
    return this.kpiChartDataStore.getToggleState(kpiKey, toggleChartId);
  }

  initializeToggleStatesForKpi(kpiKey: KpiKeys, chartData: KpiChartData): void {
    if (!chartData.charts?.toggleChart) return;

    const existingStates = this.kpiChartDataStore.getKpiToggleStates(kpiKey);

    chartData.charts.toggleChart.forEach((toggleChart) => {
      // Only set default if no existing state
      if (!existingStates || !existingStates[toggleChart.identifier]) {
        // Default to primary chart
        this.setToggleState(kpiKey, toggleChart.identifier, toggleChart.charts.primaryChart.id);
      }
    });
  }

  resetToggleStatesForKpi(kpiKey: KpiKeys, chartData: KpiChartData): void {
    if (chartData.charts?.toggleChart) {
      chartData.charts.toggleChart.forEach((toggleChart) => {
        this.setToggleState(kpiKey, toggleChart.identifier, toggleChart.charts.primaryChart.id);
      });
    }

    if (chartData.charts?.trendchart?.hasToggle) {
      const trendChart = chartData.charts.trendchart;
      const defaultState = trendChart.identifier + '_percent';
      this.setToggleState(kpiKey, trendChart.identifier, defaultState as KpiChartId);
    }
  }

  applyTrendChartToggleState(kpiKey: KpiKeys, trendData: any): boolean {
    if (!trendData.hasToggle) return false;

    const savedState = this.getToggleState(kpiKey, trendData.identifier);

    if (!savedState) return false;

    const expectedCountId = trendData.identifier + '_count';
    const shouldBeCount = savedState === expectedCountId;

    if (trendData.isCount === shouldBeCount) return false; // No change needed

    trendData.isCount = shouldBeCount;

    if (shouldBeCount) {
      trendData.isPercentChart = false;
      if (
        trendData.legend &&
        !trendData.legend.includes('On track') &&
        !this.excludesOnTrackFromLegendInCount(kpiKey)
      ) {
        trendData.legend.push('On track');
      } else if (
        trendData.legend &&
        trendData.legend.includes('On track') &&
        this.excludesOnTrackFromLegendInCount(kpiKey)
      ) {
        this.excludeValueFromLegend('On track', trendData);
      }
    } else {
      if (!trendData.targetValue) {
        this.excludeValueFromLegend('On track', trendData);
      } else if (trendData.targetValue && !trendData.legend.includes('On track')) {
        trendData.legend.push('On track');
      }
      trendData.isPercentChart =
        trendData.initialIsPercentChart !== undefined ? trendData.initialIsPercentChart : true;
    }

    return true;
  }

  hasValidChartData(chartData: KpiChartData | null): boolean {
    if (!chartData?.charts) {
      return false;
    }

    const { charts } = chartData;

    // Check if any of the chart types have valid data
    const hasBarChart = Boolean(charts.bar && charts.bar.length > 0);
    const hasPieChart = Boolean(charts.pie && charts.pie.length > 0);
    const hasTrendChart = Boolean(charts.trendchart?.series && charts.trendchart.series.length > 0);
    const hasToggleChart = Boolean(charts.toggleChart && charts.toggleChart.length > 0);
    const hasTableData = Boolean(
      charts.keyMilestonesTable?.rows && charts.keyMilestonesTable.rows.length > 0
    );

    return hasBarChart || hasPieChart || hasTrendChart || hasToggleChart || hasTableData;
  }

  private excludesOnTrackFromLegendInCount(kpiKey: KpiKeys): boolean {
    return KPI_EXCLUDES_ON_TRACK_IN_COUNT.includes(kpiKey);
  }

  private excludeValueFromLegend(excludedItem: string, trendData: any) {
    trendData.legend = trendData.legend.filter((item: string) => item !== excludedItem);
  }
}
